import { useState } from "react";
import { Plus, Pencil, Trash2, Search, Columns3, ArrowUp, ArrowDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { RecordDialog } from "./RecordDialog";
import { useResource } from "../lib/hooks";
import { toast } from "sonner";

const WIDTHS = { narrow: "w-24 max-w-[6rem]", normal: "", wide: "min-w-[16rem]" };

function useColumns(tid, columns) {
  const key = `bos-cols:${tid}`;
  const [cfg, setCfg] = useState(() => { try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; } });
  const put = (c) => { setCfg(c); localStorage.setItem(key, JSON.stringify(c)); };
  const order = [...(cfg.order || []).filter((k) => columns.some((c) => c.key === k)), ...columns.map((c) => c.key).filter((k) => !(cfg.order || []).includes(k))];
  const all = order.map((k) => columns.find((c) => c.key === k));
  return { all, cfg, put, order, visible: all.filter((c) => !(cfg.hidden || []).includes(c.key)) };
}

function ColumnMenu({ tid, cols }) {
  const { all, cfg, put, order } = cols;
  const hidden = cfg.hidden || [];
  const move = (i, d) => { const o = [...order]; [o[i], o[i + d]] = [o[i + d], o[i]]; put({ ...cfg, order: o }); };
  const width = (k) => { const w = cfg.width || {}; const n = { normal: "wide", wide: "narrow", narrow: "normal" }[w[k] || "normal"]; put({ ...cfg, width: { ...w, [k]: n } }); };
  return (
    <Popover><PopoverTrigger asChild><Button variant="outline" size="sm" data-testid={`${tid}-columns-btn`}><Columns3 className="h-4 w-4 mr-1" />Columns</Button></PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-1" data-testid={`${tid}-columns-menu`}>
        <div className="text-xs text-muted-foreground mb-1">Show, order and size columns (saved for you on this device)</div>
        {all.map((c, i) => (
          <div key={c.key} className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="accent-[#C2A462]" checked={!hidden.includes(c.key)} onChange={(e) => put({ ...cfg, hidden: e.target.checked ? hidden.filter((k) => k !== c.key) : [...hidden, c.key] })} data-testid={`${tid}-col-toggle-${c.key}`} />
            <span className="flex-1 truncate">{c.label}</span>
            <button className="text-[10px] text-[#C2A462]" onClick={() => width(c.key)} data-testid={`${tid}-col-width-${c.key}`}>{(cfg.width || {})[c.key] || "normal"}</button>
            <button disabled={i === 0} onClick={() => move(i, -1)} className="disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
            <button disabled={i === all.length - 1} onClick={() => move(i, 1)} className="disabled:opacity-30" data-testid={`${tid}-col-down-${c.key}`}><ArrowDown className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <button className="pt-1 text-xs text-slate-400 hover:text-white" onClick={() => put({})}>Reset columns</button>
      </PopoverContent></Popover>
  );
}

export function ResourceManager({ resource, label, fields, columns: allColumns, params, defaults = {}, rowActions, onRowClick, testid, drawer = false }) {
  const { items, loading, save, remove } = useResource(resource, params);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const tid = testid || resource;
  const cols = useColumns(tid, allColumns);
  const columns = cols.visible;
  const wcls = (k) => WIDTHS[(cols.cfg.width || {})[k] || "normal"];
  const shown = items.filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));

  const onSave = async (form) => {
    await save({ ...defaults, ...form }, edit?.id);
    toast.success(`${label} saved`);
  };

  return (
    <div className="space-y-4" data-testid={`${tid}-manager`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid={`${tid}-search`} className="pl-9" placeholder={`Search ${label.toLowerCase()}…`} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <span className="overline">{shown.length} records</span>
        <div className="ml-auto flex gap-2"><ColumnMenu tid={tid} cols={cols} />
        <Button data-testid={`${tid}-add-btn`} onClick={() => setEdit({ ...defaults })}><Plus className="h-4 w-4 mr-1" />New {label}</Button></div>
      </div>
      <div className="rounded-md border bg-card/60 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => <TableHead key={c.key} className={`overline ${wcls(c.key)}`}>{c.label}</TableHead>)}
              <TableHead className="text-right overline">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={columns.length + 1} className="text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && !shown.length && (
              <TableRow><TableCell colSpan={columns.length + 1} className="py-10 text-center text-muted-foreground" data-testid={`${tid}-empty`}>No {label.toLowerCase()} records yet.</TableCell></TableRow>
            )}
            {shown.map((r) => (
              <TableRow key={r.id} data-testid={`${tid}-row-${r.id}`} className={onRowClick ? "cursor-pointer" : ""} onClick={() => onRowClick?.(r)}>
                {columns.map((c) => <TableCell key={c.key} className={`text-sm ${wcls(c.key)}`}>{c.render ? c.render(r) : String(r[c.key] ?? "—")}</TableCell>)}
                <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {rowActions?.(r)}
                  <Button size="icon" variant="ghost" data-testid={`${tid}-edit-${r.id}`} onClick={() => setEdit(r)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`${tid}-delete-${r.id}`} onClick={() => window.confirm("Delete this record?") && remove(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {edit && <RecordDialog open onOpenChange={(o) => !o && setEdit(null)} title={edit.id ? `Edit ${label}` : `New ${label}`} fields={fields} initial={edit} onSave={onSave} drawer={drawer} />}
    </div>
  );
}
